import { useApiCall } from "hooks";
import { FC, useRef, useState, useEffect } from "react";
import { deleteProfilePublishAPI, postProfilePublishAPI } from "services";
import { ProfileProps } from "types";
import Modal from "ui-molecules/Modal/Modal";
import { ProcessingResults } from "./PublishModal";

type PublishModalProcessingProps = {
  profiles: ProfileProps[];
  onFinish: (results: ProcessingResults) => void;
  action: "publish" | "unpublish";
};

export const PublishModalProcessing: FC<PublishModalProcessingProps> = ({
  profiles,
  onFinish,
  action,
}) => {
  const processingStarted = useRef(false);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [currentProfile, setCurrentProfile] = useState<ProfileProps | null>(
    null
  );

  const [postProfilePublish] = useApiCall(postProfilePublishAPI, true);
  const [deleteProfilePublish] = useApiCall(deleteProfilePublishAPI, true);

  const progressPercentage =
    profiles.length > 0
      ? Math.round((currentIndex / profiles.length) * 100)
      : 0;

  useEffect(() => {
    if (processingStarted.current) {
      return;
    }

    processingStarted.current = true;

    const processPublications = async () => {
      let currentResults: ProcessingResults = {
        successfulCount: 0,
        failedCount: 0,
        errors: [],
      };

      for (let i = 0; i < profiles.length; i++) {
        const profile = profiles[i];

        setCurrentIndex(i);
        setCurrentProfile(profile);

        try {
          const response = action === "publish" ? await postProfilePublish({
            broker_profile: profile.id,
          }) : await deleteProfilePublish(profile.id);

          if (response.error) {
            currentResults = {
              ...currentResults,
              failedCount: currentResults.failedCount + 1,
              errors: [
                ...currentResults.errors,
                { id: profile.id, message: response.error },
              ],
            };
          } else {
            currentResults = {
              ...currentResults,
              successfulCount: currentResults.successfulCount + 1,
            };
          }
        } catch (err: any) {
          const errorMessage = err?.message || "Unknown error occurred";

          currentResults = {
            ...currentResults,
            failedCount: currentResults.failedCount + 1,
            errors: [
              ...currentResults.errors,
              { id: profile.id, message: errorMessage },
            ],
          };
        }
      }

      if (profiles.length > 0) {
        setCurrentIndex(profiles.length);
      }

      onFinish(currentResults);
    };

    processPublications();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getProfileName = (profile: ProfileProps | null) => {
    if (!profile) return "...";
    return `${profile.country?.name || ""} - ${profile.language_name || ""}`;
  };

  return (
    <Modal.Body>
      <div className="w-full">
        <div className="mb-6">
          <h3 className="text-lg font-medium text-gray-800 mb-3">
            {action === "publish" ? "Publishing Profiles" : "Unpublishing Profiles"}
          </h3>

          <div className="w-full bg-gray-200 rounded-full h-4 mb-2">
            <div
              className="bg-blue-600 h-4 rounded-full transition-all duration-300 ease-in-out"
              style={{ width: `${progressPercentage}%` }}
              aria-valuenow={progressPercentage}
              aria-valuemin={0}
              aria-valuemax={100}
              role="progressbar"
            />
          </div>

          <div className="flex justify-between text-sm text-gray-600 mb-4">
            <span>
              Progress: {currentIndex} of {profiles.length} profiles
            </span>
            <span>{progressPercentage}%</span>
          </div>

          <div className="p-3 bg-gray-50 rounded-md mb-6">
            <div className="flex items-center">
              <div className="animate-pulse w-3 h-3 bg-blue-500 rounded-full mr-2"></div>
              <span className="font-medium">Currently {action === "publish" ? "publishing:" : "unpublishing:"}</span>
              <span className="ml-2">{getProfileName(currentProfile)}</span>
            </div>
          </div>
        </div>
      </div>
    </Modal.Body>
  );
};